/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import {
  ChevronLeft,
  MapPin,
  Calendar,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  PhoneCall,
  TruckIcon,
  MapPinIcon,
  ArrowUpRight,
  Banknote,
  Share2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Users,
  User,
  Mail,
  Phone,
  Shirt,
  Heart,
  ChevronDown,
  ChevronUp,
  Ticket,
  Download,
  Info,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';
import {
  API_URL,
  ATTRIBUTE_KEYS,
  ORDER_ATTRIBUTE_KEYS,
  ORDER_STATUS_KEYS,
  PAYMENT_MODE,
  USER_ROLES,
  getStatusBadge,
} from '../../constant/constant';
import { useUser } from '../../contexts/UserContext';
import BiglocalLoader from '../common/BigLocalLoader';
import CancelOrderModal from '../modals/CancelOrderModel';
import RescheduleOrderModal from '../modals/RescheduleOrderModal';
import EditOrderModal from '../modals/EditOrderModal';
import BottomNav from '../layouts/Footer/BottomNav';

const CHARGE_DISPLAY_NAMES = {
  DELIVERY_CHARGE: 'Delivery Fee',
  PLATFORM_FEE: 'Platform Fee',
  SERVICE_FEE: 'Service Fee',
  PACKAGING_FEE: 'Packaging Fee',
  HANDLING_FEE: 'Handling Fee',
  TAX: 'GST',
  CONVENIENCE_FEE: 'Convenience Fee',
};

const StatusBadge = ({ status, type = 'status' }) => {
  if (type === 'paid') {
    return (
      <div className="inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-50 text-green-700">
        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
        No Dues
      </div>
    );
  }

  if (type === 'unpaid') {
    return (
      <div className="inline-flex items-center gap-1 px-2 sm:px-2 py-1 rounded-full text-xs sm:text-xs font-medium bg-red-50 text-red-700">
        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        Payment Due
      </div>
    );
  }

  // Default status badge
  return (
    <div className="inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-50 text-green-700">
      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
      {status || ''}
    </div>
  );
};

// Event Ticket Card Component - Responsive layout with left summary and right participant tickets
const EventTicketCard = ({
  orderDetails,
  displayItems,
  total,
  attributeModels,
  onCancel,
  onAccept,
  onMarkAsPaid,
  onShare,
  isUpdatingOrder,
}) => {
  const [expandedParticipants, setExpandedParticipants] = useState({});
  const { user } = useUser();
  const charges = orderDetails?.charges || [];
  const subtotal = displayItems.reduce(
    (acc, item) =>
      acc +
      (item.netPrice || item.mrp || 0) *
        (item.orderItemParticipantInfoModels?.length || item.quantity || 1),
    0,
  );

  // Download ticket as image with enhanced UI
  const handleDownloadTicket = async (item, participant, pIndex) => {
    try {
      const ticketId = `${participant?.id}`;

      // Event banner for the left panel circle — probe in CORS mode with a
      // cache-busting param so a previously cached non-CORS copy cannot fail
      const bannerUrl =
        item?.eventBannerUrl || item?.productImageUrls?.[0] || '';
      const bannerImg = await new Promise((resolve) => {
        if (!bannerUrl) return resolve(null);
        const busted = `${bannerUrl}${bannerUrl.includes('?') ? '&' : '?'}ticket-cors=1`;
        const img = document.createElement('img');
        const timer = setTimeout(() => resolve(null), 4000);
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          clearTimeout(timer);
          resolve(img);
        };
        img.onerror = () => {
          clearTimeout(timer);
          resolve(null);
        };
        img.src = busted;
      });

      // Runner icon fallback — same SVG used in the VeggShopee-Ui ticket
      const RUNNER_ICON_SVG = '<svg height="70px" width="70px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 511.697 511.697" xml:space="preserve" fill="#3a3836" stroke="#3a3836"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g transform="translate(0 1)"> <g> <path style="fill:#f8760d;" d="M505.189,119.472L505.189,119.472c-7.81-7.81-13.885-9.546-21.695-1.736l-45.125,45.993 c-6.075,6.075-14.753,6.075-20.827,0.868l-63.349-59.01c-4.339-4.339-10.414-6.942-16.488-6.942h-94.59l-79.837,75.498 c-7.81,7.81-9.546,21.695-1.736,30.373c8.678,9.546,22.563,8.678,31.241,0l59.01-62.481h43.39L147.656,306.916H62.612 c-13.885,0-26.902,10.414-27.769,24.298c-0.868,15.62,11.281,27.77,26.034,27.77H165.88c10.414,0,19.959-4.339,27.77-12.149 l57.275-61.614l61.614,65.085L296.05,456.177c-2.603,12.149,2.603,25.166,13.885,30.373c15.62,6.942,32.108-1.736,36.447-17.356 l25.166-135.376c0.868-6.075-0.868-11.281-5.207-15.62l-71.159-72.027l69.424-69.424l45.993,45.993 c7.81,7.81,21.695,7.81,29.505,0l19.959-19.959l45.125-52.068C513.867,142.035,513.867,128.15,505.189,119.472"></path> <path style="fill:#f8760d;" d="M462.667,118.605l-0.868-0.868c-7.81-7.81-1.736-7.81-10.414,0l-34.712,45.993 c-6.075,6.075-14.753,6.075-20.827,0.868l-73.763-59.01c-4.339-4.339-4.339-6.942-10.414-6.942h-68.556l-79.837,75.498 c-7.81,7.81-9.546,21.695-1.736,30.373c8.678,9.546,4.339,8.678,12.149,0l60.746-62.481h34.712L121.623,306.916h-59.01 c-13.885,0-26.902,10.414-27.769,24.298c-0.868,15.62,11.281,27.77,26.034,27.77h95.458l69.424-72.895l60.746,64.217 l-16.488,105.871c-2.603,12.149,2.603,25.166,13.885,30.373c15.62,6.942,16.488-1.736,19.959-17.356l20.827-144.922 l-55.539-78.102l63.349-69.424l40.786,45.993c7.81,7.81,18.224-3.471,26.034-11.281l8.678-8.678l54.671-54.671 C470.477,140.299,470.477,126.415,462.667,118.605"></path> </g> <g> <path style="fill:#b87a00;" d="M323.819,324.272l-20.827,144.922c-2.603,11.281-3.471,19.092-10.414,19.959 c12.149,0.868,23.431-7.81,26.034-19.959l27.77-144.922l-77.234-77.234L323.819,324.272z"></path> <path style="fill:#b87a00;" d="M487.833,118.605l-0.868-0.868c-7.81-7.81-19.092-7.81-27.77-1.736 c0.868,0.868,0.868,0.868,1.736,1.736l0.868,0.868c7.81,7.81,7.81,20.827,0,29.505l-54.671,54.671l-8.678,8.678 c-4.339,4.339-8.678,9.546-13.885,12.149c7.81,6.942,20.827,6.942,28.637-0.868l19.959-19.959l54.671-54.671 C495.643,140.299,495.643,127.282,487.833,118.605"></path> </g> <g> <path style="fill:#f8760d;" d="M163.277,207.12c-0.868-0.868-1.736-1.736-2.603-2.603c-7.81-8.678-6.942-21.695,1.736-30.373 l80.705-75.498H217.08l-79.837,75.498c-7.81,7.81-9.546,21.695-1.736,30.373S155.467,213.194,163.277,207.12"></path> <path style="fill:#f8760d;" d="M34.843,331.215c0.868-13.885,13.017-24.298,26.902-24.298H35.711 c-13.885,0-26.902,10.414-27.769,24.298c0,15.62,12.149,27.77,26.902,27.77h26.034C46.124,358.984,33.975,346.835,34.843,331.215"></path> </g> <path style="fill:#f8760d;" d="M425.351,63.933c0-24.298-19.092-43.39-43.39-43.39c-0.868,0-26.034,19.092-26.034,43.39 c0,0.868,1.736,43.39,26.034,43.39C406.26,107.323,425.351,88.232,425.351,63.933"></path> <g> <path style="fill:#b87a00;" d="M399.318,20.543c-2.603,0-6.075,0-8.678,0.868c19.959,4.339,34.712,21.695,34.712,42.522 s-14.753,38.183-34.712,42.522c2.603,0.868,6.075,0.868,8.678,0.868c24.298,0,43.39-19.092,43.39-43.39 S423.616,20.543,399.318,20.543"></path> <path style="fill:#b87a00;" d="M356.795,71.743c0,0.868,0,0.868,0,1.736C356.795,72.611,356.795,71.743,356.795,71.743"></path> </g> <path d="M295.182,497.832c-5.207,0-9.546-0.868-14.753-3.471c-14.753-6.942-23.431-23.431-19.092-40.786l15.62-100.664 l-52.068-54.671l-62.481,66.82c-1.736,1.736-3.471,2.603-6.075,2.603H34.843c-9.546,0-18.224-4.339-25.166-10.414 c-6.942-6.942-10.414-16.488-9.546-26.034c0.868-18.224,16.488-32.976,36.447-32.976h81.573l32.108-35.58 c3.471-3.471,8.678-3.471,12.149-0.868c3.471,3.471,3.471,8.678,0.868,12.149l-34.712,39.051c-2.603,1.736-4.339,2.603-6.942,2.603 H36.579c-10.414,0-19.092,6.942-19.092,16.488c0,5.207,1.736,9.546,4.339,13.017c3.471,3.471,7.81,5.207,13.017,5.207h118.02 l65.953-70.292c1.736-1.736,4.339-2.603,6.075-2.603c2.603,0,4.339,0.868,6.075,2.603l61.614,64.217 c1.736,1.736,2.603,4.339,2.603,6.942l-16.488,105.871c-1.736,9.546,1.736,18.224,9.546,21.695c5.207,2.603,10.414,1.736,14.753,0 c4.339-2.603,7.81-6.942,9.546-11.281l26.034-139.715l-74.63-74.631c-2.603-2.603-3.471-4.339-3.471-6.942 c0-2.603,0.868-4.339,2.603-6.075l69.424-69.424c3.471-3.471,8.678-3.471,12.149,0l45.993,45.993 c4.339,4.339,12.149,4.339,17.356,0l74.63-74.631c4.339-4.339,4.339-12.149,0-16.488l-0.868-0.868 c-5.207-5.207-13.017-5.207-17.356,0l-45.125,45.993c-8.678,8.678-23.431,9.546-32.976,0.868l-63.349-59.01 c-2.603-2.603-6.942-4.339-10.414-4.339h-91.986l-77.234,73.763c-5.207,5.207-5.207,13.017-0.868,18.224 c2.603,2.603,6.075,4.339,9.546,4.339l0,0c3.471,0,6.942-1.736,8.678-4.339l59.01-62.481c1.736-1.736,4.339-2.603,6.075-2.603 h43.39c3.471,0,6.942,1.736,7.81,5.207s0.868,6.942-1.736,9.546l-69.424,78.102c-3.471,3.471-8.678,4.339-12.149,0.868 c-3.471-3.471-3.471-8.678-0.868-12.149l56.407-63.349h-19.959l-56.407,59.878c-5.207,6.075-13.017,9.546-21.695,9.546l0,0 c-8.678,0-16.488-3.471-21.695-9.546c-10.414-11.281-9.546-30.373,1.736-42.522l79.837-76.366c1.736-3.471,3.471-4.339,6.075-4.339 h94.59c8.678,0,16.488,3.471,22.563,8.678l63.349,59.01c2.603,2.603,6.075,2.603,8.678,0l45.125-45.993 c11.281-11.281,30.373-11.281,42.522,0l0.868,0.868c11.281,11.281,11.281,30.373,0,41.654l-74.63,74.631 c-11.281,11.281-30.373,11.281-41.654,0l-39.919-39.919l-57.275,57.275l72.027,72.027c1.736,1.736,2.603,5.207,2.603,7.81 L329.026,470.93c-2.603,10.414-9.546,19.092-18.224,23.431C305.595,496.964,300.389,497.832,295.182,497.832z"></path> <path d="M187.575,241.832c0-5.207-3.471-8.678-8.678-8.678s-8.678,3.471-8.678,8.678s3.471,8.678,8.678,8.678 S187.575,247.038,187.575,241.832"></path> <path d="M399.318,116.001c-28.637,0-52.068-23.431-52.068-52.068s23.431-52.068,52.068-52.068 c28.637,0,52.068,23.431,52.068,52.068S427.955,116.001,399.318,116.001z M399.318,29.221c-19.092,0-34.712,15.62-34.712,34.712 s15.62,34.712,34.712,34.712c19.092,0,34.712-15.62,34.712-34.712S418.409,29.221,399.318,29.221z"></path> </g> </g></svg>';
      const runnerIconImg = await new Promise((resolve) => {
        const img = document.createElement('img');
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(RUNNER_ICON_SVG);
      });
      // Create canvas for ticket
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Unable to create ticket image.');
        return;
      }

      const scale = 3;
      const baseWidth = 1200;
      const baseHeight = 670;
      canvas.width = baseWidth * scale;
      canvas.height = baseHeight * scale;
      ctx.scale(scale, scale);

      // Helpers
      const roundRect = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      };
      const fitText = (text, maxWidth) => {
        let t = String(text ?? '');
        if (ctx.measureText(t).width <= maxWidth) return t;
        while (ctx.measureText(t + '...').width > maxWidth && t.length > 0) {
          t = t.slice(0, -1);
        }
        return t + '...';
      };
      try {
        ctx.letterSpacing = '0px';
      } catch {
        /* unsupported */
      }

      // Page background
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, baseWidth, baseHeight);

      // Ticket body (rounded white card with shadow)
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 8;
      ctx.fillStyle = '#ffffff';
      roundRect(0, 0, baseWidth, baseHeight, 16);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // Left purple panel clipped to ticket shape
      ctx.save();
      roundRect(0, 0, baseWidth, baseHeight, 16);
      ctx.clip();
      const leftGradient = ctx.createLinearGradient(0, 0, 0, baseHeight);
      leftGradient.addColorStop(0, '#a855f7');
      leftGradient.addColorStop(0.5, '#963d87');
      leftGradient.addColorStop(1, '#7c2d6f');
      ctx.fillStyle = leftGradient;
      ctx.fillRect(0, 0, 300, baseHeight);
      ctx.restore();

      // Dashed separator between panels
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.moveTo(300, 10);
      ctx.lineTo(300, baseHeight - 10);
      ctx.stroke();
      ctx.setLineDash([]);

      // ---- Left content ----
      ctx.textAlign = 'center';

      // Banner circle (image cover-fit, icon fallback)
      ctx.save();
      ctx.beginPath();
      ctx.arc(150, 112, 55, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fill();
      ctx.clip();
      if (bannerImg) {
        const iw = bannerImg.naturalWidth || bannerImg.width;
        const ih = bannerImg.naturalHeight || bannerImg.height;
        const s = Math.max(110 / iw, 110 / ih);
        ctx.drawImage(
          bannerImg,
          150 - (iw * s) / 2,
          112 - (ih * s) / 2,
          iw * s,
          ih * s,
        );
      } else if (runnerIconImg) {
        ctx.drawImage(runnerIconImg, 150 - 35, 112 - 35, 70, 70);
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.font = '44px Arial, sans-serif';
        ctx.fillText('🎫', 150, 128);
      }
      ctx.restore();

      // Labels
      try {
        ctx.letterSpacing = '2.5px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 11px Arial, sans-serif';
      ctx.fillText('MARATHON EVENT', 150, 225);
      try {
        ctx.letterSpacing = '0px';
      } catch {
        /* unsupported */
      }

      // Event name (up to 2 lines)
      const eventWords = String(item.productName || '')
        .toUpperCase()
        .split(/\s+/)
        .filter(Boolean);
      const nameLines = [];
      let curLine = '';
      ctx.font = '900 24px Arial, sans-serif';
      for (const w of eventWords) {
        const test = curLine ? `${curLine} ${w}` : w;
        if (ctx.measureText(test).width > 240 && curLine) {
          nameLines.push(curLine);
          curLine = w;
          if (nameLines.length === 2) break;
        } else {
          curLine = test;
        }
      }
      if (curLine && nameLines.length < 2) nameLines.push(curLine);
      if (eventWords.join(' ') !== nameLines.join(' ') && nameLines.length > 0) {
        nameLines[nameLines.length - 1] = fitText(
          nameLines[nameLines.length - 1] + '...',
          240,
        );
      }
      ctx.fillStyle = '#ffffff';
      nameLines.forEach((l, i) => ctx.fillText(fitText(l, 240), 150, 258 + i * 32));

      // Event date lives on the vendor's category whose name matches this

      // item's subCategory (same rule as the backend). Sources in order:

      // item.eventDate > selectedVendor.vendorCategories > attributes

      const vendorCategoryDate = (() => {

        try {

          const vendor = JSON.parse(localStorage.getItem('selectedVendor') || 'null');

          const flat = (nodes) =>

            (nodes || []).flatMap((n) => [n, ...flat(n?.subCategories)]);

          const match = flat(vendor?.vendorCategories).find(

            (c) => c?.name === item?.subCategory,

          );

          return match?.eventDate || '';

        } catch {

          return '';

        }

      })();

      const eventDateAttr =

        item?.eventDate ||

        vendorCategoryDate ||
        orderDetails?.attributeModels?.find(
          (attr) =>
            attr.name ===
            (ORDER_ATTRIBUTE_KEYS.EVENT_DATE || ATTRIBUTE_KEYS.EVENT_DATE),
        )?.value ||
        attributeModels?.find(
          (attr) =>
            attr.name ===
            (ORDER_ATTRIBUTE_KEYS.EVENT_DATE || ATTRIBUTE_KEYS.EVENT_DATE),
        )?.value;
      const eventDate = eventDateAttr ? new Date(eventDateAttr) : null;
      const eventDateStr =
        eventDate && !isNaN(eventDate.getTime())
          ? eventDate.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          : 'NA';

      try {
        ctx.letterSpacing = '2.5px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 11px Arial, sans-serif';
      ctx.fillText('EVENT DATE', 150, 385);
      try {
        ctx.letterSpacing = '0px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 18px Arial, sans-serif';
      ctx.fillText(eventDateStr, 150, 415);

      // Registration ID box
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      roundRect(30, 545, 240, 80, 10);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 1;
      roundRect(30, 545, 240, 80, 10);
      ctx.stroke();
      try {
        ctx.letterSpacing = '2.5px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = '700 10px Arial, sans-serif';
      ctx.fillText('REGISTRATION ID', 150, 573);
      try {
        ctx.letterSpacing = '0px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = '#ffffff';
      ctx.font = '900 20px Arial, sans-serif';
      ctx.fillText(fitText(`#${ticketId}`, 210), 150, 604);

      // ---- Right section ----
      ctx.textAlign = 'left';
      const rightX = 350;
      const rightW = 810; // up to 1160

      // Status badge (top right pill)
      const isPaid =
        orderDetails?.paymentStatus === 'paid' ||
        orderDetails?.status === 'Paid';
      const statusText = isPaid ? 'CONFIRMED' : 'PENDING';
      const statusBg = isPaid ? '#dcfce7' : '#fef3c7';
      const statusColor = isPaid ? '#16a34a' : '#d97706';
      ctx.font = '800 15px Arial, sans-serif';
      const statusWidth = ctx.measureText(statusText).width + 44;
      ctx.fillStyle = statusBg;
      roundRect(1160 - statusWidth, 56, statusWidth, 36, 10);
      ctx.fill();
      ctx.fillStyle = statusColor;
      ctx.fillText(statusText, 1160 - statusWidth + 22, 80);

      // Participant name
      const fullName = `${participant.firstName} ${participant.lastName}`;
      ctx.fillStyle = '#7c2d6f';
      ctx.font = '900 42px Arial, sans-serif';
      const nameMaxW = 1160 - statusWidth - 20 - rightX;
      ctx.fillText(fitText(fullName, Math.max(nameMaxW, 200)), rightX, 96);

      // Ticket type line
      try {
        ctx.letterSpacing = '1.5px';
      } catch {
        /* unsupported */
      }
      ctx.fillStyle = '#6b7280';
      ctx.font = '700 15px Arial, sans-serif';
      ctx.fillText(
        fitText(
          `TICKET #${pIndex + 1}${
            item.variant && item.variant !== 'Default'
              ? ' • ' + item.variant
              : ''
          }`.toUpperCase(),
          rightW,
        ),
        rightX,
        128,
      );
      try {
        ctx.letterSpacing = '0px';
      } catch {
        /* unsupported */
      }

      // Event name (subCategory) under ticket type
      const eventName = item?.subCategory || '';
      if (eventName) {
        ctx.fillStyle = '#7c2d6f';
        ctx.font = '800 20px Arial, sans-serif';
        ctx.fillText(fitText(eventName, rightW), rightX, 160);
      }

      // Divider
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(rightX, 185);
      ctx.lineTo(1160, 185);
      ctx.stroke();

      // Age (or derived from dob)
      let displayAge = participant.age;
      if (!displayAge && participant.dob) {
        const dobParts = participant.dob.split('-');
        if (dobParts.length === 3) {
          const dob = new Date(dobParts[2], dobParts[1] - 1, dobParts[0]);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            age--;
          }
          displayAge = age;
        }
      }

      // Pastel detail cards (2-column grid)
      const detailFields = [
        {
          label: 'EMAIL ADDRESS',
          value: participant.emailId || 'N/A',
          bg: '#e8f4fd',
          color: '#1d4ed8',
        },
        {
          label: 'PHONE',
          value: participant.mobileNo || 'N/A',
          bg: '#e9f9ef',
          color: '#15803d',
        },
      ];
      if (displayAge) {
        detailFields.push({
          label: 'AGE',
          value: `${displayAge} yrs`,
          bg: '#fdf4e3',
          color: '#b45309',
        });
      }
      if (participant.sex) {
        detailFields.push({
          label: 'GENDER',
          value: participant.sex,
          bg: '#f4ecfd',
          color: '#9333ea',
        });
      }
      if (participant.tshirtSize) {
        detailFields.push({
          label: 'T-SHIRT SIZE',
          value: participant.tshirtSize,
          bg: '#fdeef6',
          color: '#db2777',
        });
      }
      if (participant.bloodGroup) {
        detailFields.push({
          label: 'BLOOD GROUP',
          value: participant.bloodGroup,
          bg: '#fee2e2',
          color: '#b91c1c',
        });
      }
      if (participant.emergencyContact) {
        detailFields.push({
          label: 'EMERGENCY CONTACT',
          value: `${
            participant.emergencyContactName
              ? participant.emergencyContactName + ' - '
              : ''
          }${participant.emergencyContact}`,
          bg: '#fdeeee',
          color: '#dc2626',
        });
      }

      const colW = 395;
      const cardH = 62;
      const cardGap = 20;
      detailFields.forEach((f, i) => {
        const x = rightX + (i % 2) * (colW + cardGap);
        const y = 205 + Math.floor(i / 2) * (cardH + 18);
        ctx.fillStyle = f.bg;
        roundRect(x, y, colW, cardH, 12);
        ctx.fill();
        ctx.fillStyle = f.color;
        ctx.font = '800 11px Arial, sans-serif';
        ctx.fillText(f.label, x + 16, y + 24);
        ctx.fillStyle = '#111827';
        ctx.font = '700 16px Arial, sans-serif';
        ctx.fillText(fitText(String(f.value), colW - 32), x + 16, y + 48);
      });

      // Footer bar
      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(300, 610, 900, 60);
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(300, 610);
      ctx.lineTo(1200, 610);
      ctx.stroke();

      const sellerName = orderDetails?.vendorShopName || 'Event Organizer';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#6b7280';
      ctx.font = '700 13px Arial, sans-serif';
      ctx.fillText('Organized By:', rightX, 646);
      const obW = ctx.measureText('Organized By:').width;
      ctx.font = '13px Arial, sans-serif';
      ctx.fillText(fitText(` ${sellerName}`, 380), rightX + obW, 646);

      const orderDateAttr = attributeModels?.find(
        (attr) => attr.name === ORDER_ATTRIBUTE_KEYS.ORDER_DATE,
      )?.value;
      const orderDateValue = orderDateAttr || orderDetails?.createdAt;
      const parsedDate = new Date(orderDateValue);
      const orderDateStr = !isNaN(parsedDate.getTime())
        ? parsedDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : 'N/A';
      ctx.textAlign = 'right';
      ctx.font = '700 13px Arial, sans-serif';
      ctx.fillText(`Booking Date: ${orderDateStr}`, 1160, 646);

      // Download
      const link = document.createElement('a');
      const sanitizedProductName = item.productName.replace(
        /[^a-zA-Z0-9]/g,
        '_',
      );
      const sanitizedName = `${participant.firstName}_${participant.lastName}`.replace(
        /[^a-zA-Z0-9_]/g,
        '',
      );
      link.download = `Ticket_${sanitizedProductName}_${sanitizedName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Ticket downloaded successfully!');
    } catch (error) {
      console.error('Error downloading ticket:', error);
      toast.error('Failed to download ticket. Please try again.');
    }
  };

  const getAttributeValue = (key) => {
    const attr = attributeModels?.find((attr) => attr.name === key);
    return attr?.value;
  };

  const orderDate = new Date(
    getAttributeValue(ORDER_ATTRIBUTE_KEYS.ORDER_DATE) || orderDetails?.createdAt
  );
  const formattedDate = orderDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = orderDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const isPaid =
    orderDetails?.paymentStatus === 'paid' || orderDetails?.status === ORDER_STATUS_KEYS.PAID;
  const isCancelled = orderDetails?.status === ORDER_STATUS_KEYS.CANCELLED;
  // Sync with order tracking: confirmed when status is NOT PENDING (and not CANCELLED)
  const isConfirmed = ![ORDER_STATUS_KEYS.PENDING, ORDER_STATUS_KEYS.CANCELLED].includes(
    orderDetails?.status
  );
  const isPending = orderDetails?.status === ORDER_STATUS_KEYS.PENDING;

  const totalParticipants = displayItems.reduce(
    (acc, item) => acc + (item.orderItemParticipantInfoModels?.length || 0),
    0
  );

  return (
    <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT SIDE - Main Booking Summary */}
        <div className="w-full lg:w-[380px] lg:flex-shrink-0">
          <div className="lg:sticky lg:top-4 space-y-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-[#963d87] to-[#963d87] px-5 py-4 text-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-5 h-5" />
                    <span className="font-semibold">Event Booking</span>
                  </div>
                  <div
                    className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      isCancelled
                        ? 'bg-red-100 text-red-700'
                        : isConfirmed
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {isCancelled ? 'Cancelled' : isConfirmed ? 'Confirmed' : 'Pending'}
                  </div>
                </div>
                <p className="text-white/80 text-sm">Order #{orderDetails?.orderId}</p>
              </div>

              <div className="relative h-3 bg-white overflow-hidden"></div>

              <div className="px-5 pb-2 pt-0 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Booked On</p>
                    <p className="font-semibold text-gray-800">{formattedDate}</p>
                    <p className="text-xs text-gray-500">{formattedTime}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Total Amount</p>
                      <div className="relative group">
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer" />
                        <div className="hidden group-hover:block absolute right-full top-1/2 -translate-y-1/2 mr-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 p-3 text-sm z-50">
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-gray-700">
                              <span>Subtotal</span>
                              <span>₹{subtotal}</span>
                            </div>
                            {/* Discount */}
                            {orderDetails?.discount && orderDetails.discount > 0 && (
                                <div className="flex justify-between text-green-500">
                                  <span>
                                    Coupon {orderDetails?.couponCode ? `(${orderDetails.couponCode})` : ""}
                                  </span>
                                  <span>
                                    -₹{Math.round(orderDetails.couponDiscount * 100) / 100}
                                  </span>
                                </div>
                            )}
                            {charges.map((charge, index) => (
                              <div key={index} className="flex justify-between text-gray-700">
                                <span>{CHARGE_DISPLAY_NAMES[charge.name] || charge.name}</span>
                                <span>₹{charge.value}</span>
                              </div>
                            ))}
                            <div className="flex justify-between font-bold text-gray-900 pt-1.5 border-t border-gray-100">
                              <span>Total</span>
                              <span>₹{total}</span>
                            </div>
                          </div>
                          <div className="absolute left-full top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-white border-t border-r border-gray-100 rotate-45" />
                        </div>
                      </div>
                    </div>
                    <p className="font-bold text-green-600 text-xl">₹{total}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Total Tickets</p>
                    <p className="font-bold text-gray-900 text-lg">{totalParticipants}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Tickets</p>
                    <p className="font-bold text-gray-900 text-lg">{displayItems.length}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                    Booked Tickets
                  </p>
                  <div className="space-y-2">
                    {displayItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-[#d9b0d2] rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                            <Ticket className="w-4 h-4 text-[#963d87]" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{item.productName}</p>
                            {item.variant && item.variant !== 'Default' && (
                              <p className="text-xs text-gray-800">{item.variant}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            ×{item.orderItemParticipantInfoModels?.length || item.quantity}
                          </p>
                          <p className="text-xs text-gray-800">
                            ₹{(item.netPrice || item.mrp) * item.quantity}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Customer Contact */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-orange-500" />
                      <div>
                        <p className="text-xs text-gray-400">Customer</p>
                        <p className="font-semibold text-gray-900 text-sm">
                          {orderDetails?.contactNo}
                        </p>
                      </div>
                    </div>
                    <a
                      href={`tel:${orderDetails?.contactNo}`}
                      className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs font-medium transition-colors"
                    >
                      Call
                    </a>
                  </div>
                </div>

                {/* Action Buttons - All in one row */}
                <div className="flex gap-2">
                  {/* Accept Order - Show for pending event orders */}
                  {isPending && (
                    <button
                      onClick={onAccept}
                      disabled={isUpdatingOrder}
                      className="flex-1 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {isUpdatingOrder ? 'Accepting...' : 'Accept'}
                    </button>
                  )}

                  {/* Mark as Paid - Show for confirmed but not paid orders */}
                  {isConfirmed && !isPaid && orderDetails?.status !== ORDER_STATUS_KEYS.PAID && (
                    <button
                      onClick={onMarkAsPaid}
                      disabled={isUpdatingOrder}
                      className="flex-1 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                    >
                      <Banknote className="w-4 h-4" />
                      {isUpdatingOrder ? 'Processing...' : 'Mark Paid'}
                    </button>
                  )}

                  {/* Cancel Order - Show for non-cancelled and non-paid event orders */}
                  {!isCancelled && orderDetails?.status !== ORDER_STATUS_KEYS.PAID && (
                    <button
                      onClick={onCancel}
                      className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel
                    </button>
                  )}

                  <button
                    onClick={onShare}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium flex items-center justify-center gap-1 hover:bg-gray-50 transition-colors"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Participant Tickets grouped by Event */}
        <div className="flex-1 space-y-6">
          {displayItems.map((item, itemIndex) => {
            const participants = item.orderItemParticipantInfoModels || [];
            if (participants.length === 0) return null;

            return (
              <div key={itemIndex} className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gradient-to-r from-[#963d87] to-[#963d87] text-white px-4 py-2 rounded-full shadow-md">
                    <Ticket className="w-4 h-4" />
                    <span className="font-semibold text-sm">{item.productName}</span>
                    {item.variant && item.variant !== 'Default' && (
                      <span className="text-white/80 text-xs">• {item.variant}</span>
                    )}
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-orange-200 to-transparent" />
                  <span className="text-sm text-gray-500 font-medium">
                    {participants.length} ticket{participants.length > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {participants.map((participant, pIndex) => (
                    <div
                      key={pIndex}
                      className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                    >
                      <div className="bg-gradient-to-r from-[#963d87] to-[#963d87] px-4 py-3 text-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-bold">
                                {participant.firstName} {participant.lastName}
                              </p>
                              <p className="text-xs text-white/80">
                                Ticket #{pIndex + 1} of {participants.length}
                              </p>
                            </div>
                          </div>
                          <div className="text-right bg-white/10 rounded-lg px-2 py-1">
                            <p className="text-[10px] text-white/70 uppercase">Ticket ID</p>
                            <p className="font-mono text-xs font-bold">{participant?.id}</p>
                          </div>
                        </div>
                      </div>

                      {/* Event/Product Name Banner */}
                      <div className="bg-[#f5ddf1] px-4 py-2 border-b border-orange-200">
                        <div className="flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-orange-600" />
                          <p className="font-semibold text-orange-800 text-sm truncate">
                            {item.productName}
                          </p>
                          {item.variant && item.variant !== 'Default' && (
                            <span className="text-xs text-orange-600 bg-orange-200 px-2 py-0.5 rounded-full">
                              {item.variant}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="relative h-3 bg-white overflow-hidden">
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-100 rounded-full -ml-1.5" />
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-100 rounded-full -mr-1.5" />
                        <div className="border-t border-dashed border-gray-200 absolute top-1/2 left-3 right-3" />
                      </div>

                      <div className="px-4 py-3 space-y-3">
                        <div className="flex flex-wrap gap-3 text-sm">
                          {participant.emailId && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-xs">{participant.emailId}</span>
                            </div>
                          )}
                          {participant.mobileNo && (
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <Phone className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-xs">{participant.mobileNo}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            let displayAge = participant.age;
                            // Calculate age from DOB if age is not provided
                            if (!displayAge && participant.dob) {
                              const dobParts = participant.dob.split('-');
                              if (dobParts.length === 3) {
                                const dob = new Date(dobParts[2], dobParts[1] - 1, dobParts[0]);
                                const today = new Date();
                                let age = today.getFullYear() - dob.getFullYear();
                                const monthDiff = today.getMonth() - dob.getMonth();
                                if (
                                  monthDiff < 0 ||
                                  (monthDiff === 0 && today.getDate() < dob.getDate())
                                ) {
                                  age--;
                                }
                                displayAge = age;
                              }
                            }
                            return displayAge ? (
                              <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                <span className="text-gray-400">Age:</span>
                                <strong>{displayAge}</strong>
                              </span>
                            ) : null;
                          })()}
                          {participant.dob && (
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                              <span className="text-gray-400">DOB:</span>
                              <strong>{participant.dob}</strong>
                            </span>
                          )}
                          {participant.sex && (
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                              <strong>{participant.sex}</strong>
                            </span>
                          )}
                          {participant.tshirtSize && (
                            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">
                              <Shirt className="w-3 h-3" />
                              <strong>{participant.tshirtSize}</strong>
                            </span>
                          )}
                          {participant.bloodGroup && (
                            <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs">
                              <Heart className="w-3 h-3" />
                              <strong>{participant.bloodGroup}</strong>
                            </span>
                          )}
                        </div>

                        {participant.emergencyContact && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-blue-50 rounded-lg px-2.5 py-1.5">
                            <PhoneCall className="w-3.5 h-3.5 text-blue-500" />
                            <span>
                              Emergency: <strong>{participant.emergencyContact}</strong>
                            </span>
                          </div>
                        )}

                        <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5">
                          <div className="flex items-start gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="leading-relaxed">
                                {[
                                  participant?.addressLine1,
                                  participant?.addressLine2,
                                  participant?.city,
                                  participant?.state,
                                  participant?.pinCode,
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                              </span>
                              {participant?.nationality && (
                                <span className="ml-1 text-gray-500">
                                  • {participant?.nationality}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`px-4 py-2 flex items-center justify-between ${
                          isConfirmed ? 'bg-green-50' : 'bg-orange-50'
                        }`}
                      >
                        <p
                          className={`text-xs font-medium ${
                            isConfirmed ? 'text-green-600' : 'text-orange-600'
                          }`}
                        >
                          {isConfirmed ? '✓ Confirmed' : '⏳ Pending'}
                        </p>
                        <button
                          onClick={() => handleDownloadTicket(item, participant, pIndex)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f5ddf1] hover:bg-[#e9c5e3] text-[#963d87] rounded-full text-xs font-medium transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Payment Difference Alert Component
const PaymentDifferenceAlert = ({ attributeModels }) => {
  const paymentDiff = parseFloat(
    attributeModels?.find((attr) => attr.name === 'Payment Difference')?.value || '0'
  );
  const diffType = attributeModels?.find((attr) => attr.name === 'Payment Difference Type')?.value;
  const originalTotal = parseFloat(
    attributeModels?.find((attr) => attr.name === 'Original Order Total')?.value || '0'
  );
  const updatedTotal = parseFloat(
    attributeModels?.find((attr) => attr.name === 'Updated Order Total')?.value || '0'
  );

  if (!paymentDiff || paymentDiff === 0) return null;

  const isRefund = diffType === 'REFUND_DUE';
  const isAdditional = diffType === 'ADDITIONAL_PAYMENT_REQUIRED';

  return (
    <div
      className={`rounded-lg border-2 p-4 ${
        isRefund
          ? 'bg-blue-50 border-blue-200'
          : isAdditional
            ? 'bg-orange-50 border-orange-200'
            : 'bg-gray-50 border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-full ${
            isRefund ? 'bg-blue-100' : isAdditional ? 'bg-orange-100' : 'bg-gray-100'
          }`}
        >
          {isRefund ? (
            <TrendingDown className="size-5 text-blue-600" />
          ) : (
            <TrendingUp className="size-5 text-orange-600" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className={`size-4 ${isRefund ? 'text-blue-600' : 'text-orange-600'}`} />
            <h4 className={`font-semibold ${isRefund ? 'text-blue-900' : 'text-orange-900'}`}>
              {isRefund ? 'Refund Due to Customer' : 'Additional Payment Required'}
            </h4>
          </div>
          <p className={`text-sm mb-3 ${isRefund ? 'text-blue-700' : 'text-orange-700'}`}>
            {isRefund
              ? 'Order was updated and the new total is less than the original payment.'
              : 'Order was updated and the new total exceeds the original payment.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="text-gray-600 text-xs mb-1">Original Total</p>
              <p className="font-semibold text-gray-900">₹{originalTotal}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="text-gray-600 text-xs mb-1">Updated Total</p>
              <p className="font-semibold text-gray-900">₹{updatedTotal}</p>
            </div>
            <div
              className={`rounded-lg p-3 border-2 ${
                isRefund ? 'bg-blue-100 border-blue-300' : 'bg-orange-100 border-orange-300'
              }`}
            >
              <p className={`text-xs mb-1 ${isRefund ? 'text-blue-700' : 'text-orange-700'}`}>
                {isRefund ? 'Refund Amount' : 'Additional Amount'}
              </p>
              <p className={`font-bold text-lg ${isRefund ? 'text-blue-900' : 'text-orange-900'}`}>
                ₹{Math.abs(paymentDiff)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Order Tracking Component
const OrderTracking = ({ attributeModels, currentStatus, isEventOrder = false }) => {
  const getAttributeValue = (key) => {
    const attr = attributeModels?.find((attr) => attr.name === key);
    return attr?.value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    };
  };

  const orderDate = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.ORDER_DATE));
  const deliveredOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON));
  const cancelledOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.CANCELLED_ON));
  const rescheduledOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.RESCHEDULED_ON));
  const approvedOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON));
  const outForDeliveryOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON));
  const paidOn = formatDate(getAttributeValue(ORDER_ATTRIBUTE_KEYS.PAID_ON));

  const isCancelled = [ORDER_STATUS_KEYS.CANCELLED].includes(currentStatus);

  // Determine which steps were completed before cancellation
  const wasApproved = !!approvedOn;
  const wasOutForDelivery = !!outForDeliveryOn;
  const wasDelivered = !!deliveredOn;

  // Event Order Tracking Steps
  // Event orders flow: Booked (pending) -> Confirmed (accepted) -> Completed (paid/delivered)
  const eventTrackingSteps = [
    {
      id: 'booked',
      title: 'Booked',
      icon: Ticket,
      status: 'completed',
      date: orderDate,
      isActive: true,
    },
    {
      id: 'confirmed',
      title: 'Confirmed',
      icon: CheckCircle,
      status: [ORDER_STATUS_KEYS.PENDING].includes(currentStatus) ? 'pending' : 'completed',
      date: paidOn || approvedOn,
      isActive: ![ORDER_STATUS_KEYS.PENDING].includes(currentStatus),
    },
  ];

  // Define tracking steps based on the flow: Pending -> Approved -> Out For Delivery -> Delivered -> Paid
  const allTrackingSteps = [
    {
      id: 'pending',
      title: 'Received',
      icon: Package,
      status: 'completed',
      date: orderDate,
      isActive: true,
    },
    {
      id: 'accepted',
      title: currentStatus === ORDER_STATUS_KEYS.RESCHEDULED ? 'Rescheduled' : 'Accepted',
      icon: currentStatus === ORDER_STATUS_KEYS.RESCHEDULED ? Calendar : CheckCircle,
      status: [ORDER_STATUS_KEYS.PENDING].includes(currentStatus)
        ? 'pending'
        : currentStatus === ORDER_STATUS_KEYS.RESCHEDULED
          ? 'rescheduled'
          : 'completed',
      date: currentStatus === ORDER_STATUS_KEYS.RESCHEDULED ? rescheduledOn : approvedOn,
      isActive: Object.values(ORDER_STATUS_KEYS).includes(currentStatus),
    },
    {
      id: 'outForDelivery',
      title: 'Out For Delivery',
      icon: Truck,
      status: [ORDER_STATUS_KEYS.RESCHEDULED].includes(currentStatus)
        ? 'skipped'
        : [ORDER_STATUS_KEYS.PENDING, ORDER_STATUS_KEYS.ACCEPTED].includes(currentStatus)
          ? 'pending'
          : 'completed',
      date: [
        ORDER_STATUS_KEYS.OUT_FOR_DELIVERY,
        ORDER_STATUS_KEYS.DELIVERED,
        ORDER_STATUS_KEYS.PAID,
      ].includes(currentStatus)
        ? outForDeliveryOn
        : null,
      isActive: [
        ORDER_STATUS_KEYS.OUT_FOR_DELIVERY,
        ORDER_STATUS_KEYS.DELIVERED,
        ORDER_STATUS_KEYS.PAID,
      ].includes(currentStatus),
    },
    {
      id: 'delivered',
      title: ORDER_STATUS_KEYS.DELIVERED,
      icon: CheckCircle,
      status: [ORDER_STATUS_KEYS.RESCHEDULED].includes(currentStatus)
        ? 'skipped'
        : ![ORDER_STATUS_KEYS.DELIVERED, 'Completed', ORDER_STATUS_KEYS.PAID].includes(
              currentStatus
            )
          ? 'pending'
          : 'completed',
      date: deliveredOn,
      isActive: [ORDER_STATUS_KEYS.DELIVERED, ORDER_STATUS_KEYS.PAID].includes(currentStatus),
    },
  ];

  // Build tracking steps based on order type and cancellation
  let trackingSteps = [];

  if (isCancelled) {
    if (isEventOrder) {
      // Event order cancelled flow: Confirmed -> Cancelled (simple 2-step flow)
      trackingSteps.push(eventTrackingSteps[0]); // Confirmed
      // Add Cancelled as the final step (no intermediate steps for event orders)
      trackingSteps.push({
        id: 'cancelled',
        title: 'Cancelled',
        icon: XCircle,
        status: 'cancelled',
        date: cancelledOn,
        isActive: true,
      });
    } else {
      // Regular order cancelled flow
      trackingSteps.push(allTrackingSteps[0]);

      // Show Accepted if it was approved before cancellation
      if (wasApproved) {
        trackingSteps.push({
          ...allTrackingSteps[1],
          status: 'completed',
          title: 'Accepted',
          icon: CheckCircle,
        });
      }

      // Show Out For Delivery if it reached that stage before cancellation
      if (wasOutForDelivery) {
        trackingSteps.push({
          ...allTrackingSteps[2],
          status: 'completed',
          date: outForDeliveryOn,
          isActive: true,
        });
      }

      // Show Delivered if it was delivered before cancellation
      if (wasDelivered) {
        trackingSteps.push({
          ...allTrackingSteps[3],
          status: 'completed',
          date: deliveredOn,
        });
      }

      // Add Cancelled as the final step
      trackingSteps.push({
        id: 'cancelled',
        title: 'Cancelled',
        icon: XCircle,
        status: 'cancelled',
        date: cancelledOn,
        isActive: true,
      });
    }
  } else {
    // Not cancelled - use appropriate steps based on order type
    trackingSteps = isEventOrder ? eventTrackingSteps : allTrackingSteps;
  }

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white';
      case 'pending':
        return 'bg-gray-200 text-gray-500';
      case 'cancelled':
        return 'bg-red-500 text-white';
      case 'rescheduled':
        return 'bg-orange-500 text-white';
      case 'accepted':
        return 'bg-gray-100 text-gray-400';
      default:
        return 'bg-gray-200 text-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-2 sm:px-4 py-1.5 md:py-1">
      {/* Horizontal Tracking - All Screens */}
      <div className="w-full">
        <div className="flex items-start justify-between relative">
          {trackingSteps.map((step, index) => {
            const Icon = step.icon;
            const isLast = index === trackingSteps.length - 1;

            return (
              <div key={step.id} className="flex flex-col items-center relative flex-1">
                {/* Connector Line */}
                {!isLast && (
                  <div className="absolute top-4  sm:top-5 md:top-6 left-1/2 w-full h-0.5 bg-gray-200 z-0">
                    <div
                      className={`h-full transition-all duration-500 ${
                        // If current step is completed and next step is completed/rescheduled, show full line
                        (step.status === 'completed' || step.status === 'rescheduled') &&
                        (trackingSteps[index + 1]?.status === 'completed' ||
                          trackingSteps[index + 1]?.status === 'rescheduled' ||
                          trackingSteps[index + 1]?.status === 'cancelled')
                          ? trackingSteps[index + 1]?.status === 'cancelled'
                            ? 'w-full bg-red-500'
                            : 'w-full bg-green-500'
                          : // If current step is completed, show half line
                            step.status === 'completed' || step.status === 'rescheduled'
                            ? 'w-1/2 bg-green-500'
                            : // If current step is cancelled, show half red line
                              step.status === 'cancelled'
                              ? 'w-1/2 bg-red-500'
                              : 'w-0 bg-green-500'
                      }`}
                    />
                  </div>
                )}

                {/* Step Icon */}
                <div
                  className={`relative z-10 flex-shrink-0 size-8 sm:size-10 md:size-12 rounded-full flex items-center justify-center ${getStepColor(step.status)} transition-all duration-300 mb-2 sm:mb-3`}
                >
                  <Icon className="size-5 sm:size-6" />
                </div>

                {/* Step Content */}
                <div className="text-center max-w-[120px] sm:max-w-none">
                  <h4
                    className={`text-[11px] sm:text-sm font-medium ${step.isActive ? 'text-gray-900' : 'text-gray-500'} mb-1`}
                  >
                    {step.title}
                  </h4>
                  {/* <p
                    className={`text-xs ${step.isActive ? 'text-gray-600' : 'text-gray-400'} hidden sm:block mb-2`}
                  >
                    {step.description}
                  </p> */}

                  {/* Date */}
                  {step.date && (
                    <div className="text-center">
                      <p className="text-[11px] sm:text-xs font-medium text-gray-900">
                        {step.date.date}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-500">{step.date.time}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function OrderDetailsPage() {
  const params = useParams();
  const orderId = params.id;
  const { user } = useUser();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [attributeModels, setAttributeModels] = useState([]);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [expandedParticipants, setExpandedParticipants] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();
  const { state: customerData } = useLocation();

  // Extract variant name from image URL
  const extractVariantFromImageUrl = (imageUrl) => {
    try {
      // Extract the part between last %2F (or /) and the file extension
      const parts = imageUrl.split(/[\/]|%2F/);
      const lastPart = parts[parts.length - 1];
      const variantName = lastPart.split('.')[0];
      return variantName.toLowerCase().trim();
    } catch {
      return '';
    }
  };

  // Find matching image URL for a variant
  const findMatchingImageUrl = (variantName, productImageUrls) => {
    if (!variantName || !productImageUrls || productImageUrls.length === 0) {
      const images = Array.isArray(productImageUrls)
        ? productImageUrls
        : [productImageUrls || '/images/imagenotfound.webp'];
      return images[0] || '/images/imagenotfound.webp';
    }

    const normalizedVariant = variantName.toLowerCase().trim();
    const images = Array.isArray(productImageUrls) ? productImageUrls : [productImageUrls];

    const matchingImage = images.find((imageUrl) => {
      const imageVariant = extractVariantFromImageUrl(imageUrl);
      return imageVariant === normalizedVariant;
    });

    // Return matching image, or first image if no match
    return matchingImage || images[0] || '/images/imagenotfound.webp';
  };

  const fetchOrderDetails = async () => {
    if (!orderId) return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `${API_URL.BASE_ORDER}/rest/big-local/api/v1/order/details?orderId=${orderId}`,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken') || '',
            'X-USER-ROLE': 'VENDOR',
            'Content-Type': 'application/json',
          },
        }
      );
      setOrderDetails(response.data);
      setAttributeModels(response?.data?.attributeModels);
    } catch (err) {
      console.error('Error fetching order details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (status, additionalData = {}) => {
    try {
      setIsUpdatingOrder(true);
      const response = await axiosInstance.put(
        `${API_URL.BASE_ORDER}/rest/big-local/api/v1/order/status`,
        {
          orderId: orderId,
          status: status,
          ...additionalData,
        },
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        }
      );

      const result = await response.data;
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating order status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update order status',
      };
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const handleCancelOrder = async (cancellationReason) => {
    const apiAttributes =
      orderDetails?.attributeModels?.map((attr) => {
        if (attr.name === ORDER_ATTRIBUTE_KEYS.CANCELLED_ON) {
          return { ...attr, value: new Date().toISOString() };
        }
        if (attr.name === ORDER_ATTRIBUTE_KEYS.CANCELLED_BY) {
          return { ...attr, value: USER_ROLES.VENDOR };
        }
        return attr;
      }) || [];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.CANCELLED, {
      cancellationReason,
      attributeModels: apiAttributes,
    });

    if (result.success) {
      setIsCancelModalOpen(false);
      // toast.success('Order Cancelled Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to cancel order. Please try again.');
    }
  };

  const handleRescheduleOrder = async (newDeliveryDate, selectedSlot) => {
    const isServiceOrder = getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) === 'true';

    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    };

    const updatedAttributeModels =
      orderDetails.attributeModels?.map((attr) => {
        // For service orders, only update SERVICE_PICKUP_TIME and RESCHEDULED_ON
        if (isServiceOrder) {
          if (attr.name === ORDER_ATTRIBUTE_KEYS.SERVICE_PICKUP_TIME && selectedSlot) {
            // Format: "HH:MM AM/PM - HH:MM AM/PM on Day Month Date Year"
            const [start, end] = selectedSlot.split('-');
            const [startHour, startMinute] = start.split(':').map(Number);
            const [endHour, endMinute] = end.split(':').map(Number);

            const startTime = new Date(newDeliveryDate);
            startTime.setHours(startHour, startMinute, 0, 0);

            const endTime = new Date(newDeliveryDate);
            endTime.setHours(endHour, endMinute, 0, 0);

            const pickupDateTime = `${formatTime(startTime)} - ${formatTime(endTime)} on ${newDeliveryDate.toDateString()}`;
            return { ...attr, value: pickupDateTime };
          } else if (attr.name === ORDER_ATTRIBUTE_KEYS.RESCHEDULED_ON) {
            return { ...attr, value: new Date().toISOString() };
          }
          // Keep DELIVERY_TIME unchanged for service orders
          return attr;
        } else {
          // For regular orders, update DELIVERY_TIME with selected slot and RESCHEDULED_ON
          if (attr.name === ORDER_ATTRIBUTE_KEYS.DELIVERY_TIME) {
            if (selectedSlot) {
              // Use the selected time slot
              const [start, end] = selectedSlot.split('-');
              const [startHour, startMinute] = start.split(':').map(Number);
              const [endHour, endMinute] = end.split(':').map(Number);

              const startTime = new Date(newDeliveryDate);
              startTime.setHours(startHour, startMinute, 0, 0);

              const endTime = new Date(newDeliveryDate);
              endTime.setHours(endHour, endMinute, 0, 0);

              const deliveryDateTime = `${formatTime(startTime)} - ${formatTime(endTime)} on ${newDeliveryDate.toDateString()}`;
              return { ...attr, value: deliveryDateTime };
            } else {
              // Fallback to shop closing time if no slot selected
              const { end } = JSON.parse(
                user?.attributeValues?.find((attr) => attr.name === 'shopTiming')?.value ||
                  '{"end": "20:00"}'
              );
              let [endHour, endMinute] = end.split(':').map(Number);
              const deliveryTime = new Date(newDeliveryDate);
              deliveryTime.setHours(endHour, endMinute, 0, 0);
              const deliveryDateTime = `by ${formatTime(deliveryTime)} on ${newDeliveryDate.toDateString()}`;
              return { ...attr, value: deliveryDateTime };
            }
          } else if (attr.name === ORDER_ATTRIBUTE_KEYS.RESCHEDULED_ON) {
            return { ...attr, value: new Date().toISOString() };
          }
          return attr;
        }
      }) || [];

    const apiAttributes = [...updatedAttributeModels];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.RESCHEDULED, {
      attributeModels: apiAttributes,
    });

    if (result.success) {
      setIsRescheduleModalOpen(false);
      // toast.success('Order Rescheduled Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to reschedule order. Please try again.');
    }
  };

  const handleMarkAsCompleted = async () => {
    const apiAttributes =
      orderDetails?.attributeModels?.map((attr) => {
        if (attr.name === ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) {
          return { ...attr, value: new Date().toISOString() };
        }
        return attr;
      }) || [];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.DELIVERED, {
      attributeModels: apiAttributes,
    });

    if (result.success) {
      // toast.success('Order Delivered Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to mark order as delivered. Please try again.');
    }
  };

  const handleMarkAsPaid = async () => {
    const apiAttributes =
      orderDetails?.attributeModels?.map((attr) => {
        if (attr.name === ORDER_ATTRIBUTE_KEYS.PAID_ON) {
          return { ...attr, value: new Date().toISOString() };
        }

        if (attr.name === ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) {
          if (!attr.value) {
            return { ...attr, value: new Date().toISOString() };
          }
        }
        return attr;
      }) || [];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.PAID, {
      attributeModels: apiAttributes,
    });

    if (result?.success) {
      // toast.success('Order Paid Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to mark order as paid. Please try again.');
    }
  };

  const handleAcceptOrder = async () => {
    const apiAttributes =
      orderDetails?.attributeModels?.map((attr) => {
        if (attr.name === ORDER_ATTRIBUTE_KEYS.APPROVED_ON) {
          return { ...attr, value: new Date().toISOString() };
        }
        return attr;
      }) || [];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.ACCEPTED, {
      attributeModels: apiAttributes,
    });

    if (result.success) {
      // toast.success('Order Accepted Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to approve order. Please try again.');
    }
  };

  const handleMarkOutForDelivery = async () => {
    const apiAttributes =
      orderDetails?.attributeModels?.map((attr) => {
        if (attr.name === ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) {
          return { ...attr, value: new Date().toISOString() };
        }
        return attr;
      }) || [];

    const result = await updateOrderStatus(ORDER_STATUS_KEYS.OUT_FOR_DELIVERY, {
      attributeModels: apiAttributes,
    });

    if (result.success) {
      // toast.success('Order Out For Delivery Successfully');
      fetchOrderDetails();
    } else {
      toast.error(result.error || 'Failed to mark order as delivered. Please try again.');
    }
  };

  const handleUpdateOrder = async (updatedOrderData) => {
    try {
      setIsUpdatingOrder(true);
      const response = await axiosInstance.put(
        `${API_URL.BASE_ORDER}/rest/big-local/api/v1/order?orderId=${orderId}`,
        updatedOrderData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            'X-USER-ROLE': 'VENDOR',
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        setIsEditModalOpen(false);

        // Show appropriate toast based on payment difference
        if (updatedOrderData._isPrepaid && updatedOrderData._paymentDifference !== 0) {
          if (updatedOrderData._paymentDifference > 0) {
            toast.success(
              `Order updated! Customer needs to pay additional ₹${Math.abs(updatedOrderData._paymentDifference)}`
            );
          } else {
            toast.success(
              `Order updated! Refund of ₹${Math.abs(updatedOrderData._paymentDifference)} due to customer`
            );
          }
        } else {
          toast.success('Order updated successfully');
        }

        fetchOrderDetails();
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error?.response?.data?.message || 'Failed to update order. Please try again.');
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const handleShareOnWhatsApp = () => {
    const orderDate = new Date(customerData?.orderDate || orderDetails?.createdAt).toDateString();
    const deliveryTime = getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERY_TIME);
    const deliveryMethod = attributeModels?.find(
      (attr) => attr?.name === ORDER_ATTRIBUTE_KEYS.DELIVERY_METHOD
    )?.value;
    const paymentMethod =
      orderDetails?.paymentStatus || paymentStatusAttribute || PAYMENT_MODE?.PAY_ON_DELIVERY;

    // Get vendor address from user context
    const vendorAddress = orderDetails?.vendorAddress;
    const deliveryAddress = orderDetails?.deliveryAddress || customerData?.deliveryAddress || '';

    // Build Google Maps direction link based on delivery method
    let directionsLink = '';
    const isHomeDelivery =
      deliveryMethod?.toLowerCase()?.includes('home') ||
      deliveryMethod?.toLowerCase()?.includes('delivery');

    if (vendorAddress && deliveryAddress) {
      const origin = encodeURIComponent(isHomeDelivery ? vendorAddress : deliveryAddress);
      const destination = encodeURIComponent(isHomeDelivery ? deliveryAddress : vendorAddress);

      directionsLink = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    } else if (deliveryAddress) {
      // Fallback to just showing the delivery address location
      directionsLink = `https://www.google.com/maps?q=${encodeURIComponent(deliveryAddress)}`;
    }

    // Build items list
    let itemsList = '';
    displayItems.forEach((item, index) => {
      const itemTotal = (item.netPrice || item.mrp || 0) * (item.quantity || 1);
      itemsList += `${index + 1}. ${item.productName}\n   ${item.variant} ${item.unit} × ${item.quantity} = ₹${itemTotal}\n`;
    });

    // Build the formatted message
    const message = `
*ORDER DETAILS*

• *Order ID:* #${customerData?.orderId || orderDetails?.orderId}
• *Date:* ${orderDate}
• *Status:* ${orderDetails?.status || ORDER_STATUS_KEYS.PENDING}

*CUSTOMER DETAILS*
• Phone: ${orderDetails?.contactNo}
• Address: ${deliveryAddress}
• Delivery: ${deliveryTime || 'Not specified'}

*ITEMS ORDERED*

${itemsList}
━━━━━━━━━━━━━━━━━━━━━━━━━
*PAYMENT SUMMARY*

Subtotal (${displayItems.length} items)    ₹${subtotal}
${charges.map((c) => `${CHARGE_DISPLAY_NAMES[c.name] || c.name}           ₹${c.value}`).join('\n')}
─────────────────────────
*Total Amount*             *₹${total}*

• Payment Method: ${paymentMethod}
• Delivery Method: ${deliveryMethod || 'Standard'}

${orderDetails?.note || customerData?.note ? `*NOTE*\n${orderDetails?.note || customerData?.note}` : ''}
${!directionsLink ? `*GET DIRECTIONS*\n${directionsLink}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━
Thank you for your order!
`.trim();

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);

    // Open WhatsApp with the formatted message
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user && user?.id && orderId) {
      fetchOrderDetails();
    }
  }, [user, orderId]);

  // Get items from either orderDetails or customerData
  const items = orderDetails?.items || customerData?.items || [];
  const products = customerData?.products || [];
  const displayItems = items.length > 0 ? items : products;

  const getAttributeValue = (key) => {
    const attr = attributeModels?.find((attr) => attr.name === key);
    return attr?.value;
  };

  const paymentStatusAttribute = getAttributeValue(ORDER_ATTRIBUTE_KEYS.PAYMENT_METHOD);

  // Calculate totals
  const charges = orderDetails?.charges || [];
  const subtotal = displayItems.reduce((acc, item) => {
    return acc + (item.netPrice || item.mrp || 0) * (item.quantity || 1);
  }, 0);

  const deliveryMethod = attributeModels?.find(
    (attr) => attr?.name === ORDER_ATTRIBUTE_KEYS.DELIVERY_METHOD
  )?.value;

  const isSelfPickup = deliveryMethod === 'Self Pickup';

  const total = orderDetails?.total;

  const isDineIn =
    getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_DINE_IN) === 'true' ||
    !!getAttributeValue(ORDER_ATTRIBUTE_KEYS.TABLE_NUMBER);

  // Check if this is an event order
  const isEventOrder =
    attributeModels?.find((attr) => attr.name === 'isEventOrder')?.value === 'true';

  // Check if any item has participant info
  const hasParticipantInfo = displayItems.some(
    (item) => item.orderItemParticipantInfoModels && item.orderItemParticipantInfoModels.length > 0
  );

  // Toggle participant expansion for an item
  const toggleParticipantExpansion = (itemIndex) => {
    setExpandedParticipants((prev) => ({
      ...prev,
      [itemIndex]: !prev[itemIndex],
    }));
  };

  // Show Event Ticket UI for event orders
  if ((isEventOrder || hasParticipantInfo) && !isLoading && orderDetails) {
    return (
      <>
        <div className="min-h-screen pb-20 md:pb-0 bg-gray-100 font-sans">
          {/* Back Button */}
          <div className="max-w-6xl mx-auto  px-3 sm:px-4 lg:px-6 py-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm lg:text-base transition-colors"
            >
              <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6" /> Back
            </button>
          </div>

          {/* Order Tracking */}
          <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 mb-4 lg:mb-6">
            <OrderTracking
              attributeModels={attributeModels}
              currentStatus={orderDetails?.status || customerData?.status || 'Pending'}
              isEventOrder={true}
            />
          </div>

          {/* Event Ticket Card */}
          <EventTicketCard
            orderDetails={orderDetails}
            displayItems={displayItems}
            total={total}
            attributeModels={attributeModels}
            onCancel={() => setIsCancelModalOpen(true)}
            onAccept={handleMarkAsPaid}
            onMarkAsPaid={handleMarkAsPaid}
            onShare={handleShareOnWhatsApp}
            isUpdatingOrder={isUpdatingOrder}
          />
        </div>

        <BottomNav />

        <CancelOrderModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelOrder}
          isLoading={isUpdatingOrder}
        />
        <RescheduleOrderModal
          isOpen={isRescheduleModalOpen}
          onClose={() => setIsRescheduleModalOpen(false)}
          onConfirm={handleRescheduleOrder}
          isLoading={isUpdatingOrder}
          vendorConfig={{
            deliverySlots: user?.attributeValues?.find((attr) => attr.name === 'deliverySlots')
              ?.value
              ? JSON.parse(user.attributeValues.find((attr) => attr.name === 'deliverySlots').value)
              : {},
            shopTiming: user?.attributeValues?.find((attr) => attr.name === 'shopTiming')?.value
              ? JSON.parse(user.attributeValues.find((attr) => attr.name === 'shopTiming').value)
              : { start: '08:00', end: '20:00' },
            weeklyOffDay:
              user?.attributeValues?.find((attr) => attr.name === 'weeklyOffDay')?.value || '',
          }}
          isService={getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) === 'true'}
        />
      </>
    );
  }

  // Regular Order UI
  return (
    <>
      <div className="min-h-screen bg-gray-50 font-sans">
        <div>
          {/* Order Tracking */}
          <OrderTracking
            attributeModels={attributeModels}
            currentStatus={orderDetails?.status || customerData?.status || 'Pending'}
            isEventOrder={isEventOrder || hasParticipantInfo}
          />
        </div>

        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 pt-2 md:py-2">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start md:items-center gap-3 sm:gap-4">
                <div className="w-full">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                    <h1 className="text-lg sm:text-xl lg:text-2xl flex items-center gap-2 font-bold text-gray-900 truncate">
                      <button
                        onClick={() => navigate(-1)}
                        className="p-1 md:p-2 h-fit bg-gray-100 rounded-full"
                      >
                        <ChevronLeft className="size-5 md:size-7" />
                      </button>{' '}
                      Order#{customerData?.orderId || orderDetails?.orderId || ''}
                    </h1>

                    {/* Order Status Badge */}
                    {(() => {
                      const statusBadge = getStatusBadge(
                        orderDetails?.status || ORDER_STATUS_KEYS.PENDING
                      );
                      return (
                        <div
                          className={`inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${statusBadge.bg} ${statusBadge.text}`}
                        >
                          {orderDetails?.status || ORDER_STATUS_KEYS.PENDING}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-col sm:flex-row justify-center sm:items-center gap-1 sm:gap-1 text-sm sm:text-sm text-gray-900">
                    {(() => {
                      const fullAddress =
                        orderDetails?.deliveryAddress || customerData?.deliveryAddress || '';
                      const addressParts = fullAddress.split('\\n');
                      const customerName = addressParts.length > 1 ? addressParts[0] : null;
                      return customerName ? (
                        <span className="px-2 py-0.5 w-fit text-xs font-medium bg-violet-100 text-gray-800 rounded-full">
                          {customerName}
                        </span>
                      ) : null;
                    })()}
                    <div
                      onClick={() => window.open(`tel:${orderDetails.contactNo}`)}
                      className="text-gray-900 w-fit flex gap-1"
                    >
                      <span className="font-semibold">
                        <PhoneCall className="size-5 pt-1" />
                      </span>{' '}
                      {orderDetails?.contactNo}{' '}
                      <button className="bg-green-600 text-white text-[12px] rounded-full py-0 px-3 md:px-3 ml-1">
                        Call
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Event Order Badge */}
                      {(isEventOrder || hasParticipantInfo) && (
                        <div className="px-3 py-1 w-fit text-xs font-medium bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                          <Ticket className="size-3" />
                          <span>Event Booking</span>
                        </div>
                      )}

                      {/* Dine In Badge */}
                      {isDineIn && (
                        <div className="px-3 py-1 w-fit text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                          <span>Dine In</span>
                        </div>
                      )}

                      {isSelfPickup && getAttributeValue(ORDER_ATTRIBUTE_KEYS.TABLE_NUMBER) && (
                        <div className="px-2 py-1 w-fit text-xs font-medium bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                          <span>🪑</span>
                          <span>Table {getAttributeValue(ORDER_ATTRIBUTE_KEYS.TABLE_NUMBER)}</span>
                        </div>
                      )}
                    </div>
                    {![
                      ORDER_STATUS_KEYS.DELIVERED,
                      ORDER_STATUS_KEYS.PAID,
                      ORDER_STATUS_KEYS.CANCELLED,
                    ].includes(orderDetails?.status) && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        {!isDineIn &&
                          getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) === 'true' && (
                            <span className="flex text-xs min-w-56 items-center gap-1">
                              <span className="px-2 py-0.5 rounded-full bg-green-100 font-medium">
                                Pickup Slot:{' '}
                                {getAttributeValue(ORDER_ATTRIBUTE_KEYS.SERVICE_PICKUP_TIME)}
                              </span>
                            </span>
                          )}
                        {!isDineIn &&
                          getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) !== 'true' && (
                            <span className="hidden sm:inline">•</span>
                          )}
                        {!isDineIn &&
                          getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) !== 'true' &&
                          getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERY_TIME) && (
                            <span className="flex text-xs min-w-56 items-center gap-1">
                              <span className="font-semibold">
                                <Truck className="size-5 pt-1" />
                              </span>{' '}
                              <span className="px-2 py-0.5 rounded-full bg-green-100 font-medium">
                                {getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERY_TIME)}
                              </span>
                            </span>
                          )}
                      </>
                    )}
                    {!isDineIn && <span className="hidden sm:inline">•</span>}
                    {!isDineIn &&
                      (() => {
                        const fullAddress =
                          orderDetails?.deliveryAddress || customerData?.deliveryAddress || '';
                        const addressParts = fullAddress.split('\\n');
                        const customerName = addressParts.length > 1 ? addressParts[0] : null;
                        const address =
                          addressParts.length > 1 ? addressParts.slice(1).join(', ') : fullAddress;
                        return (
                          <span className="flex text-xs items-start gap-1">
                            <span className="font-semibold">
                              <MapPinIcon className="size-5 pt-1" />
                            </span>{' '}
                            <span className="flex flex-col">
                              <span>{address}</span>
                            </span>
                          </span>
                        );
                      })()}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between lg:justify-end pb-2 md:pb-0 gap-2">
                {/* Event Order Actions - Accept and Cancel */}
                {(isEventOrder || hasParticipantInfo) && (
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    {/* Accept Order - Show for pending event orders */}
                    {orderDetails?.status === ORDER_STATUS_KEYS.PENDING && (
                      <button
                        onClick={handleAcceptOrder}
                        disabled={isUpdatingOrder}
                        className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                      >
                        {isUpdatingOrder ? 'Accepting...' : 'Accept'}
                      </button>
                    )}

                    {/* Cancel Order - Show for non-cancelled event orders */}
                    {orderDetails?.status !== ORDER_STATUS_KEYS.CANCELLED &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.PAID && (
                        <button
                          onClick={() => setIsCancelModalOpen(true)}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600"
                        >
                          Cancel
                        </button>
                      )}

                    <button
                      onClick={handleShareOnWhatsApp}
                      className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border border-green-500 bg-white text-green-600 font-semibold rounded-md ml-1 hover:bg-green-50 flex items-center gap-1"
                      title="Share on WhatsApp"
                    >
                      <Share2 className="size-4" />
                      <span className="">Share</span>
                    </button>
                  </div>
                )}

                {/* Regular Order Actions (non-event, non-dine-in) */}
                {!isDineIn && !(isEventOrder || hasParticipantInfo) && (
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    {/* Approve Order - Show until Approved On is populated */}
                    {!getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.CANCELLED && (
                        <button
                          onClick={handleAcceptOrder}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Accepting...' : 'Accept'}
                        </button>
                      )}

                    {/* Out for Delivery - Only show after order is approved and not yet out for delivery */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON) &&
                      !getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) && (
                        <button
                          onClick={handleMarkOutForDelivery}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Processing...' : 'Out for Delivery'}
                        </button>
                      )}

                    {/* Mark as Delivered - Show after out for delivery and not yet delivered */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) &&
                      !getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) && (
                        <button
                          onClick={handleMarkAsCompleted}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Delivering...' : 'Mark Delivered'}
                        </button>
                      )}

                    {/* Mark as Paid - Show after delivered and not yet paid */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.PAID && (
                        <button
                          onClick={handleMarkAsPaid}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Processing...' : 'Mark Paid'}
                        </button>
                      )}

                    {/* Cancel Order - Show for non-final states (not yet paid or cancelled) */}
                    {!getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.CANCELLED && (
                        <button
                          onClick={() => setIsCancelModalOpen(true)}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600"
                        >
                          Cancel
                        </button>
                      )}

                    {/* Reschedule Order - Show for non-final states */}

                    {![
                      ORDER_STATUS_KEYS.DELIVERED,
                      ORDER_STATUS_KEYS.PAID,
                      ORDER_STATUS_KEYS.CANCELLED,
                      ORDER_STATUS_KEYS.OUT_FOR_DELIVERY,
                      ORDER_STATUS_KEYS.PENDING,
                    ].includes(orderDetails?.status) &&
                      !isDineIn && (
                        <button
                          onClick={() => setIsRescheduleModalOpen(true)}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600"
                        >
                          Reschedule
                        </button>
                      )}

                    {/* Edit Order - Show for Pending and Accepted orders */}
                    {[
                      ORDER_STATUS_KEYS.PENDING,
                      ORDER_STATUS_KEYS.ACCEPTED,
                      ORDER_STATUS_KEYS.RESCHEDULED,
                    ].includes(orderDetails?.status) && (
                      <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border border-blue-500 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50"
                      >
                        Edit Order
                      </button>
                    )}

                    <button
                      onClick={handleShareOnWhatsApp}
                      className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border border-green-500 bg-white text-green-600 font-semibold rounded-md ml-1 hover:bg-green-50 flex items-center gap-1"
                      title="Share on WhatsApp"
                    >
                      <Share2 className="size-4" />
                      <span className="">Share</span>
                    </button>
                  </div>
                )}

                {/* Dine-In Order Actions (non-event) */}
                {isDineIn && !(isEventOrder || hasParticipantInfo) && (
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    {/* Approve Order - Show until Approved On is populated */}
                    {!getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON) && (
                      <button
                        onClick={handleAcceptOrder}
                        disabled={isUpdatingOrder}
                        className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                      >
                        {isUpdatingOrder ? 'Accepting...' : 'Accept'}
                      </button>
                    )}

                    {/* Mark as Ready - Show after order is approved and not yet ready */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON) &&
                      !getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) && (
                        <button
                          onClick={handleMarkOutForDelivery}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Processing...' : 'Mark as Ready'}
                        </button>
                      )}

                    {/* Mark as Delivered - Show after ready and not yet delivered */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) &&
                      !getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) && (
                        <button
                          onClick={handleMarkAsCompleted}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Delivering...' : 'Mark Delivered'}
                        </button>
                      )}

                    {/* Mark as Paid - Show after delivered and not yet paid */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.PAID && (
                        <button
                          onClick={handleMarkAsPaid}
                          disabled={isUpdatingOrder}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 disabled:opacity-50"
                        >
                          {isUpdatingOrder ? 'Processing...' : 'Mark Paid'}
                        </button>
                      )}

                    {/* Cancel Order - Show for non-final states (not yet paid or cancelled) */}
                    {!getAttributeValue(ORDER_ATTRIBUTE_KEYS.DELIVERED_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.CANCELLED && (
                        <button
                          onClick={() => setIsCancelModalOpen(true)}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600"
                        >
                          Cancel
                        </button>
                      )}

                    {/* Reschedule Order - Show for accepted/rescheduled orders that are not yet out for delivery */}
                    {getAttributeValue(ORDER_ATTRIBUTE_KEYS.APPROVED_ON) &&
                      !getAttributeValue(ORDER_ATTRIBUTE_KEYS.OUT_FOR_DELIVERY_ON) &&
                      orderDetails?.status !== ORDER_STATUS_KEYS.CANCELLED && (
                        <button
                          onClick={() => setIsRescheduleModalOpen(true)}
                          className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600"
                        >
                          Reschedule
                        </button>
                      )}

                    {/* Edit Order - Show for Pending and Accepted orders */}
                    {[
                      ORDER_STATUS_KEYS.PENDING,
                      ORDER_STATUS_KEYS.ACCEPTED,
                      ORDER_STATUS_KEYS.RESCHEDULED,
                    ].includes(orderDetails?.status) && (
                      <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border border-blue-500 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50"
                      >
                        Edit Order
                      </button>
                    )}

                    <button
                      onClick={handleShareOnWhatsApp}
                      className="px-2 sm:px-3 py-1.5 text-xs sm:text-xs border border-green-500 bg-white text-green-600 font-semibold rounded-md ml-1 hover:bg-green-50 flex items-center gap-1"
                      title="Share on WhatsApp"
                    >
                      <Share2 className="size-4" />
                      <span className="">Share</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <BiglocalLoader title="" />
        ) : (
          <div className="max-w-8xl mx-auto px-2 py-2 sm:px-3 sm:py-2 pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-2 sm:space-y-2">
                {/* Payment Difference Alert */}
                {orderDetails?.paymentStatus &&
                  orderDetails.paymentStatus !== PAYMENT_MODE.PAY_ON_DELIVERY && (
                    <PaymentDifferenceAlert attributeModels={attributeModels} />
                  )}

                {/* Products */}
                <div className="bg-white rounded-lg border border-gray-200 px-4 py-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Items</h3>
                  </div>

                  <div className="">
                    {displayItems.map((item, index) => {
                      // Get the matching image URL for the variant
                      const matchingImageUrl = findMatchingImageUrl(
                        item?.variant,
                        item?.productImageUrls || []
                      );

                      const hasParticipants =
                        item.orderItemParticipantInfoModels &&
                        item.orderItemParticipantInfoModels.length > 0;
                      const isExpanded = expandedParticipants[index];

                      return (
                        <div key={index} className="py-2 border-b border-gray-100 last:border-b-0">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <img
                              src={matchingImageUrl}
                              alt={item.productName}
                              onError={(e) => {
                                e.currentTarget.src = '/images/imagenotfound.webp';
                              }}
                              className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-medium text-gray-900 text-sm sm:text-base line-clamp-2">
                                    {item?.productName}
                                  </h4>
                                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    {item?.variant} {item?.unit} x {item?.quantity}
                                  </p>
                                </div>
                                <p className="font-semibold text-gray-900 text-sm sm:text-base flex-shrink-0">
                                  ₹{(item.netPrice || item.mrp || 0) * (item.quantity || 1)}
                                </p>
                              </div>

                              {/* Participant Info Toggle Button */}
                              {hasParticipants && (
                                <button
                                  onClick={() => toggleParticipantExpansion(index)}
                                  className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-orange-600 hover:text-orange-700 font-medium"
                                >
                                  <Users className="size-4" />
                                  <span>
                                    {item.orderItemParticipantInfoModels.length} Participant
                                    {item.orderItemParticipantInfoModels.length > 1 ? 's' : ''}
                                  </span>
                                  {isExpanded ? (
                                    <ChevronUp className="size-4" />
                                  ) : (
                                    <ChevronDown className="size-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expanded Participant Details */}
                          {hasParticipants && isExpanded && (
                            <div className="mt-3 ml-0 sm:ml-20 space-y-3">
                              {item.orderItemParticipantInfoModels.map((participant, pIndex) => (
                                <div
                                  key={pIndex}
                                  className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 sm:p-4"
                                >
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
                                      <User className="size-4 text-purple-600" />
                                    </div>
                                    <span className="font-semibold text-gray-900 text-sm">
                                      Participant {pIndex + 1}
                                    </span>
                                    {participant.tshirtSize && (
                                      <span className="ml-auto px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                                        <Shirt className="size-3" />
                                        {participant.tshirtSize}
                                      </span>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                    {/* Name */}
                                    <div className="flex items-center gap-2">
                                      <User className="size-4 text-gray-400" />
                                      <span className="text-gray-600">Name:</span>
                                      <span className="font-medium text-gray-900">
                                        {participant.firstName} {participant.lastName}
                                      </span>
                                    </div>

                                    {/* Email */}
                                    {participant.emailId && (
                                      <div className="flex items-center gap-2">
                                        <Mail className="size-4 text-gray-400" />
                                        <span className="text-gray-600">Email:</span>
                                        <span className="font-medium text-gray-900 truncate">
                                          {participant.emailId}
                                        </span>
                                      </div>
                                    )}

                                    {/* Mobile */}
                                    {participant.mobileNo && (
                                      <div className="flex items-center gap-2">
                                        <Phone className="size-4 text-gray-400" />
                                        <span className="text-gray-600">Mobile:</span>
                                        <span className="font-medium text-gray-900">
                                          {participant.mobileNo}
                                        </span>
                                      </div>
                                    )}

                                    {/* Age & Gender */}
                                    <div className="flex items-center gap-2">
                                      <Calendar className="size-4 text-gray-400" />
                                      <span className="text-gray-600">Age/Gender:</span>
                                      <span className="font-medium text-gray-900">
                                        {(() => {
                                          let displayAge = participant.age;
                                          // Calculate age from DOB if age is not provided
                                          if (!displayAge && participant.dob) {
                                            const dobParts = participant.dob.split('-');
                                            if (dobParts.length === 3) {
                                              const dob = new Date(
                                                dobParts[2],
                                                dobParts[1] - 1,
                                                dobParts[0]
                                              );
                                              const today = new Date();
                                              let age = today.getFullYear() - dob.getFullYear();
                                              const monthDiff = today.getMonth() - dob.getMonth();
                                              if (
                                                monthDiff < 0 ||
                                                (monthDiff === 0 && today.getDate() < dob.getDate())
                                              ) {
                                                age--;
                                              }
                                              displayAge = age;
                                            }
                                          }
                                          return `${displayAge || 'N/A'} yrs / ${participant.sex}`;
                                        })()}
                                      </span>
                                    </div>

                                    {/* DOB */}
                                    {participant.dob && (
                                      <div className="flex items-center gap-2">
                                        <Calendar className="size-4 text-gray-400" />
                                        <span className="text-gray-600">DOB:</span>
                                        <span className="font-medium text-gray-900">
                                          {participant.dob}
                                        </span>
                                      </div>
                                    )}

                                    {/* Blood Group */}
                                    {participant.bloodGroup && (
                                      <div className="flex items-center gap-2">
                                        <Heart className="size-4 text-gray-400" />
                                        <span className="text-gray-600">Blood Group:</span>
                                        <span className="font-medium text-gray-900">
                                          {participant.bloodGroup}
                                        </span>
                                      </div>
                                    )}

                                    {/* Emergency Contact */}
                                    {participant.emergencyContact && (
                                      <div className="flex items-center gap-2">
                                        <PhoneCall className="size-4 text-gray-400" />
                                        <span className="text-gray-600">Emergency:</span>
                                        <span className="font-medium text-gray-900">
                                          {participant.emergencyContact}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Address Section */}
                                  {(participant.addressLine1 ||
                                    participant.city ||
                                    participant.state) && (
                                    <div className="mt-3 pt-3 border-t border-purple-200">
                                      <div className="flex items-start gap-2">
                                        <MapPin className="size-4 text-gray-400 mt-0.5" />
                                        <div className="text-sm">
                                          <span className="text-gray-600">Address: </span>
                                          <span className="font-medium text-gray-900">
                                            {[
                                              participant.addressLine1,
                                              participant.addressLine2,
                                              participant.city,
                                              participant.state,
                                              participant.pinCode,
                                              participant.country,
                                            ]
                                              .filter(Boolean)
                                              .join(', ')}
                                          </span>
                                        </div>
                                      </div>
                                      {participant.nationality && (
                                        <div className="flex items-center gap-2 mt-1 ml-6">
                                          <span className="text-gray-600 text-sm">
                                            Nationality:
                                          </span>
                                          <span className="font-medium text-gray-900 text-sm">
                                            {participant.nationality}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-2 sm:space-y-2">
                {/* Order Note */}
                {(orderDetails?.note || customerData?.note) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">Order Note</h3>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {orderDetails?.note || customerData?.note}
                    </p>
                  </div>
                )}

                {/* Order Details */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-2 sm:gap-4 mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                      Order Summary
                    </h3>
                    {orderDetails?.status === 'Paid' || isEventOrder || hasParticipantInfo ? (
                      <StatusBadge status="Paid" type="paid" />
                    ) : (
                      <StatusBadge status="Payment Due" type="unpaid" />
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex  text-base justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600 font-medium">Subtotal</span>
                        <span className="font-medium">
                          {displayItems.length} items • ₹{subtotal}
                        </span>
                      </div>

                      {orderDetails?.couponDiscount && orderDetails.couponDiscount > 0 && (
                        <div className="flex text-base justify-between gap-1 sm:gap-0 text-green-600">
                          <span className="font-medium">
                            Coupon Discount {orderDetails.couponCode && `(${orderDetails.couponCode})`}
                          </span>
                          <span className="font-medium">
                            -₹{orderDetails.couponDiscount}
                          </span>
                        </div>
                      )}

                      {charges.map((charge, index) => (
                        <div key={index} className="flex text-base justify-between gap-1 sm:gap-0">
                          <span className="text-gray-600 font-medium">
                            {CHARGE_DISPLAY_NAMES[charge.name] || charge.name}
                          </span>
                          <span className="font-medium">₹{charge.value}</span>
                        </div>
                      ))}

                      <div className="border-t border-gray-200 pt-2 mt-3">
                        <div className="flex text-xl justify-between gap-1 sm:gap-0 font-semibold">
                          <span className="text-gray-900">Total</span>
                          <span className="text-gray-900">₹{total}</span>
                        </div>
                      </div>

                      <div className="flex text-sm justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600 font-medium">Order Type</span>
                        <span className="font-medium text-gray-900 text-right">
                          {isEventOrder || hasParticipantInfo
                            ? 'Event Booking'
                            : isDineIn
                              ? 'Dine In'
                              : attributeModels?.find(
                                  (attr) => attr?.name === ORDER_ATTRIBUTE_KEYS.DELIVERY_METHOD
                                )?.value}
                        </span>
                      </div>

                      <div className="flex text-sm justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600 font-medium">
                          {isDineIn ? 'Payment Status' : 'Payment Method'}
                        </span>
                        <div className="flex items-center gap-2">
                          {/* Event Order Payment */}
                          {(isEventOrder || hasParticipantInfo) && (
                            <>
                              <span className="text-green-600 text-xs font-bold">
                                <Banknote className="size-6" />
                              </span>
                              <span className="font-medium">Paid Online</span>
                            </>
                          )}
                          {/* Regular Order Payment */}
                          {!isDineIn && !(isEventOrder || hasParticipantInfo) && (
                            <>
                              <span className="text-green-600 text-xs font-bold">
                                {orderDetails?.paymentStatus || paymentStatusAttribute ? (
                                  <Banknote className="size-6" />
                                ) : (
                                  <TruckIcon className="size-6" />
                                )}
                              </span>
                              <span className="font-medium">
                                {orderDetails?.paymentStatus ||
                                  paymentStatusAttribute ||
                                  PAYMENT_MODE?.PAY_ON_DELIVERY}
                              </span>
                            </>
                          )}
                          {/* Dine-In Payment */}
                          {isDineIn && !(isEventOrder || hasParticipantInfo) && (
                            <span className="font-medium">
                              {orderDetails?.paymentStatus || paymentStatusAttribute === 'Prepaid'
                                ? 'paid'
                                : 'payment due'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {!isDineIn && !(isEventOrder || hasParticipantInfo) && (
                  <LocationPanel
                    address={orderDetails?.deliveryAddress || customerData?.deliveryAddress || ''}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <CancelOrderModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelOrder}
        isLoading={isUpdatingOrder}
      />
      <RescheduleOrderModal
        isOpen={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onConfirm={handleRescheduleOrder}
        isLoading={isUpdatingOrder}
        vendorConfig={{
          deliverySlots: user?.attributeValues?.find((attr) => attr.name === 'deliverySlots')?.value
            ? JSON.parse(user.attributeValues.find((attr) => attr.name === 'deliverySlots').value)
            : {},
          shopTiming: user?.attributeValues?.find((attr) => attr.name === 'shopTiming')?.value
            ? JSON.parse(user.attributeValues.find((attr) => attr.name === 'shopTiming').value)
            : { start: '08:00', end: '20:00' },
          weeklyOffDay:
            user?.attributeValues?.find((attr) => attr.name === 'weeklyOffDay')?.value || '',
        }}
        isService={getAttributeValue(ORDER_ATTRIBUTE_KEYS.IS_SERVICE) === 'true'}
      />
      <>
        <EditOrderModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onConfirm={handleUpdateOrder}
          orderDetails={orderDetails}
          isLoading={isUpdatingOrder}
        />
      </>
    </>
  );
}

function LocationPanel({ address }) {
  // Remove name prefix if address contains \n separator (format: "Name\nActual Address")
  const addressParts = address?.split('\\n') || [];
  const addressStr = addressParts.length > 1 ? addressParts.slice(1).join(', ') : address;

  const q =
    address?.latitude && address?.longitude
      ? `${address.latitude},${address.longitude}`
      : addressStr;
  const encoded = encodeURIComponent(q || '');

  const mapsUrl = `https://www.google.com/maps?q=${encoded}`;
  const embedUrl = `https://maps.google.com/maps?q=${encoded}&t=&z=18&ie=UTF8&iwloc=&output=embed`;
  const hasAddress = !!addressStr;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Shipping Address</h3>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </button>
      </div>
      <div className="relative h-96 overflow-hidden rounded-xl ring-1 ring-slate-200 md:h-[450px]">
        {/* Map Iframe serves as the background layer */}
        <iframe
          title="Vendor Map"
          src={hasAddress ? embedUrl : ''}
          className="h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        {/* Absolutely positioned info box with a modern, blurred background */}
        <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-white/80 p-4 shadow-lg backdrop-blur-sm ring-1 ring-black/5 md:left-auto md:max-w-sm">
          <div className="flex items-start gap-3">
            <MapPin className="mt-1 size-5 flex-shrink-0 text-slate-500" />
            <div>
              <p className="text-[12px] sm:text-sm text-slate-600">
                {addressStr || 'Address not available'}
              </p>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <a
              href={hasAddress ? mapsUrl : '#'}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-500 ${
                !hasAddress && 'cursor-not-allowed opacity-50'
              }`}
              onClick={(e) => !hasAddress && e.preventDefault()}
            >
              <ArrowUpRight className="size-4" /> <span>Directions</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
